/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Wade Wilkey <wade.wilkey@deliciousai.com>
 */

import { useLazyQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import { Grid2 as Grid, Stack, Typography } from '@mui/material'
import { isEmpty, isNil, round } from 'lodash'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { DaiDivider } from '@/components/DaiDivider'
import StatWithProgress from '@/components/reports/StatWithProgress'
import { RankedEmployeeStat } from '@/components/tasks/RankedEmployeeStat'
import { StoreTaskRank } from '@/components/tasks/StoreTaskRank'
import { TaskNeverDoneStat } from '@/components/tasks/TaskNeverDoneStat'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  ActionsCompletionPercentageQuery,
  ActionsCompletionPercentageQueryVariables,
  GET_ACTION_COMPLETED_OVER_TIME,
  GET_ACTIONS_COMPLETION_PERCENTAGE,
  GetActionsCompletedOverTimeQuery,
  GetActionsCompletedOverTimeQueryVariables,
  GroupByDate,
} from '@/graphql'
import { useIsSingleStore } from '@/hooks/use-is-single-store'
import { useSetDefaultTimeRange } from '@/hooks/useSetDefaultTimeRange'
import useT from '@/i18n/useT'
import { ChartHelpers } from '@/utils/chart.helpers'
import { DateHelpers } from '@/utils/date.helpers'
import { getColorForStat } from '@/utils/statCards'

import DAIOverTimeLineChart from '../charts/ApexCharts/DAIOverTimeLineChart'
import { HomeDashboardParams } from '../dashboards/Default/HomeSummary'
import Stats from '../dashboards/Default/Stats'

const gridItemProps = {
  size: { xs: 12, sm: 6, md: 6, lg: 3, xl: 3 },
  sx: { display: 'flex', flexDirection: 'column' },
}

const RestocksReport = () => {
  const { t } = useT()
  const {
    state: urlState,
    handle: { handleUpdateSearchParams },
  } = useUrlSearchParams<HomeDashboardParams>()
  const { id: singleStoreId } = useParams()
  const {
    startDate: rawStartDateNoTime,
    endDate: rawEndDateNoTime,
    storeIds: rawStoreIds,
  } = urlState

  const startDateNoTime = useMemo(
    () => rawStartDateNoTime,
    [rawStartDateNoTime],
  )
  const endDateNoTime = useMemo(() => rawEndDateNoTime, [rawEndDateNoTime])

  const startDate = useMemo(
    () =>
      startDateNoTime
        ? DateHelpers.getStartOfDayWithTimezone(startDateNoTime)
        : undefined,
    [startDateNoTime],
  )
  const endDate = useMemo(
    () =>
      endDateNoTime
        ? DateHelpers.getEndOfDayWithTimezone(endDateNoTime)
        : undefined,
    [endDateNoTime],
  )

  const storeIds = useMemo(
    () => (singleStoreId ? [singleStoreId] : rawStoreIds),
    [singleStoreId, rawStoreIds],
  )
  const storeIdsKey = useMemo(() => JSON.stringify(storeIds), [storeIds])

  const { isSingleStore } = useIsSingleStore(storeIds)

  const [
    getActionsCompletedOverTime,
    {
      data: actionsCompletedOverTimeData,
      loading: loadingActionsCompletedOverTime,
      refetch: refetchActionsCompletedOverTime,
      variables: actionsCompletedOverTimeVariables,
    },
  ] = useLazyQuery<
    GetActionsCompletedOverTimeQuery,
    GetActionsCompletedOverTimeQueryVariables
  >(GET_ACTION_COMPLETED_OVER_TIME, {
    notifyOnNetworkStatusChange: true,
  })

  const [
    getActionsCompletionPercentage,
    {
      data: actionsCompletionPercentageData,
      loading: loadingActionsCompletionPercentage,
      refetch: refetchActionsCompletionPercentage,
      variables: actionsCompletionPercentageVariables,
    },
  ] = useLazyQuery<
    ActionsCompletionPercentageQuery,
    ActionsCompletionPercentageQueryVariables
  >(GET_ACTIONS_COMPLETION_PERCENTAGE, { notifyOnNetworkStatusChange: true })

  const [
    getActionsCompletionPercentagePrev,
    {
      data: actionsCompletionPercentagePrevData,
      loading: loadingActionsCompletionPercentagePrev,
      refetch: refetchActionsCompletionPercentagePrev,
      variables: actionsCompletionPercentagePrevVariables,
    },
  ] = useLazyQuery<
    ActionsCompletionPercentageQuery,
    ActionsCompletionPercentageQueryVariables
  >(GET_ACTIONS_COMPLETION_PERCENTAGE, { notifyOnNetworkStatusChange: true })

  const handleRefresh = () => {
    if (!isEmpty(actionsCompletedOverTimeVariables)) {
      refetchActionsCompletedOverTime()
    }
    if (!isEmpty(actionsCompletionPercentageVariables)) {
      refetchActionsCompletionPercentage()
    }
    if (!isEmpty(actionsCompletionPercentagePrevVariables)) {
      refetchActionsCompletionPercentagePrev()
    }
  }
  useRefresh(handleRefresh)

  useSetDefaultTimeRange({
    startDate,
    endDate,
    updateSearchParams: handleUpdateSearchParams,
  })

  useEffect(() => {
    const setActionCompletionPercentage = async () => {
      if (startDate && endDate) {
        const commonFilters = {
          startTime: startDateNoTime,
          endTime: endDateNoTime,
          storeIds,
          isRestock: true,
        }
        getActionsCompletionPercentage({
          variables: {
            data: {
              ...commonFilters,
            },
          },
        })
        const { previousStartDate, previousEndDate } =
          DateHelpers.getPreviousDateRange(startDate, endDate)
        getActionsCompletionPercentagePrev({
          variables: {
            data: {
              ...commonFilters,
              startTime: previousStartDate.format('YYYY-MM-DD'),
              endTime: previousEndDate.format('YYYY-MM-DD'),
            },
          },
        })
        const { startDate: startTime, endDate: endTime } =
          ChartHelpers.getChartWindow(
            startDate,
            endDate,
            new Date().toISOString(),
          )
        const groupBy = ChartHelpers.getGroupBy(startTime, endTime)

        getActionsCompletedOverTime({
          variables: {
            filters: {
              ...commonFilters,
              startTime,
              endTime,
            },
            groupBy,
          },
        })
      }
    }
    setActionCompletionPercentage()
  }, [
    startDate,
    endDate,
    startDateNoTime,
    endDateNoTime,
    getActionsCompletedOverTime,
    getActionsCompletionPercentage,
    getActionsCompletionPercentagePrev,
    storeIdsKey,
  ])

  const getPercentage = (
    obj?: ActionsCompletionPercentageQuery['actionsPercentCompleted'],
  ) => {
    if (obj && obj.denominator && !isNil(obj.numerator)) {
      return obj.numerator / obj.denominator
    }
    return null
  }

  const currentCompletion = getPercentage(
    actionsCompletionPercentageData?.actionsPercentCompleted,
  )
  const previousCompletion = getPercentage(
    actionsCompletionPercentagePrevData?.actionsPercentCompleted,
  )
  const changeInCompletion =
    currentCompletion &&
    previousCompletion &&
    !DateHelpers.isSingleDayToday(startDateNoTime, endDateNoTime)
      ? round((currentCompletion - previousCompletion) * 100)
      : null

  return (
    <>
      <Stack spacing={3} my={3}>
        <Grid container spacing={3}>
          <Grid {...gridItemProps}>
            <Stats
              title={t('restocks.report.stat.restocksCompleted')}
              amount={
                isNil(currentCompletion) ? (
                  <Typography variant="h6" gutterBottom />
                ) : (
                  <StatWithProgress value={currentCompletion} />
                )
              }
              change={
                isNil(changeInCompletion)
                  ? null
                  : {
                      text: `${changeInCompletion}%`,
                      color: getColorForStat(changeInCompletion, false),
                    }
              }
              changeTitle={t('statCard.previousPeriod')}
              changeTooltip={
                startDateNoTime && endDateNoTime
                  ? DateHelpers.previousPeriodTooltipText(
                      startDateNoTime,
                      endDateNoTime,
                      t,
                    )
                  : undefined
              }
              loading={
                loadingActionsCompletionPercentage ||
                loadingActionsCompletionPercentagePrev
              }
            />
          </Grid>
          <Grid {...gridItemProps}>
            {isSingleStore ? (
              <TaskNeverDoneStat
                title={t('restocks.report.stat.neverDone')}
                storeIds={storeIds}
                startTime={startDateNoTime}
                endTime={endDateNoTime}
                isRestock
                showTime
                showCompletion
              />
            ) : (
              <StoreTaskRank
                storeIds={storeIds}
                startTime={startDateNoTime}
                endTime={endDateNoTime}
                isRestock
                rankEnd="bottom"
                onRowClicked={id =>
                  handleUpdateSearchParams({ storeIds: [id] })
                }
              />
            )}
          </Grid>
          <Grid {...gridItemProps}>
            <RankedEmployeeStat
              storeIds={storeIds}
              startTime={startDateNoTime}
              endTime={endDateNoTime}
              rankEnd="bottom"
              isRestock
            />
          </Grid>
          <Grid {...gridItemProps}>
            <RankedEmployeeStat
              storeIds={storeIds}
              startTime={startDateNoTime}
              endTime={endDateNoTime}
              rankEnd="top"
              isRestock
            />
          </Grid>
        </Grid>
        <DaiDivider />
        <DAIOverTimeLineChart
          dataGranularity={
            startDate && endDate
              ? ChartHelpers.getGroupBy(startDate, endDate)
              : GroupByDate.DAY
          }
          type={'line'}
          loading={loadingActionsCompletedOverTime}
          title={t('restocksReport.overTimeChart.title')}
          options={{
            stroke: { curve: 'smooth' },
            yaxis: {
              labels: {
                formatter: value => `${value}%`,
              },
              min: 0,
              max: 100,
            },
            annotations: {
              xaxis: ChartHelpers.getXAxisRangeAnnotation(
                t,
                startDate,
                endDate,
                startDate && endDate
                  ? ChartHelpers.getGroupBy(startDate, endDate)
                  : GroupByDate.DAY,
              ),
            },
          }}
          series={[
            {
              id: 'restockCompletion',
              dataType: 'percentage',
              name: 'Restock Completion',
              data:
                DateHelpers.filterFutureDates(
                  actionsCompletedOverTimeData?.actionsCompletedOverTime || [],
                  'date',
                ).map(d => {
                  if (
                    d.denominator &&
                    d.denominator !== 0 &&
                    !isNil(d.numerator)
                  ) {
                    return round((d.numerator / d.denominator) * 100)
                  }
                  return 0
                }) || [],
            },
          ]}
          dates={
            DateHelpers.filterFutureDates(
              actionsCompletedOverTimeData?.actionsCompletedOverTime || [],
              'date',
            ).map(d => DateHelpers.parseIsoDateWithoutTimezone(d.date)) || []
          }
        />
      </Stack>
    </>
  )
}

export default RestocksReport
